// ExamsPanel — pick a class, pick (or create) an exam, then enter marks for
// the whole class in one grid. Totals, percentage, grade and class rank are
// computed live; each row opens that student's printable report card, and
// the whole grid prints as a class result sheet.
import { useEffect, useMemo, useState } from 'react';
import { Plus, Printer, Trash2, X, FileText } from 'lucide-react';
import {
  getClasses, getClassExams, getExamGrid, saveExamMarks, deleteExam,
  getStudentResults, getStudentProfile, getClassRoster,
  getSubjects, addSubject as createSubject, patchSubject,
} from '../api/client';
import Button from '../ui/Button';
import { Card, CardHeader } from '../ui/Card';
import PrintSheet, { PrintHeader } from '../ui/PrintSheet';
import { ReportCardSheet, TraditionalReportCardSheet, REPORT_SLOTS } from './profile/ResultsTab';
import { fmtDate, resultFor, pct, todayISO } from '../ui/format';

const inputCls = 'border border-line rounded-s px-3 py-2 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit';

// The exact exam names the RBSE report card's five columns look for (see
// REPORT_SLOTS in ResultsTab.jsx) — offered as one-click buttons so "where
// do I enter First/Second/Third/Half Yearly/Annual marks" has an obvious
// answer instead of requiring the admin to guess a name that matches the
// slot-matching regex.
const RBSE_QUICK_START = [
  { key: 'first',  name: 'First Periodic Test' },
  { key: 'second', name: 'Second Periodic Test' },
  { key: 'third',  name: 'Third Periodic Test' },
  { key: 'half',   name: 'Half Yearly Exam' },
  { key: 'annual', name: 'Annual Exam' },
];

export default function ExamsPanel({ onOpenStudent, schoolName, board }) {
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [exams,   setExams]   = useState([]);
  const [examId,  setExamId]  = useState(null);      // selected existing exam id, or 'new'
  const [grid,    setGrid]    = useState(null);      // { exam:{name,exam_date,has_oral}, subjects:[{subject,max_marks,oral_max}], students:[...] }
  const [marks,   setMarks]   = useState({});        // `${student_id}|${subject}` -> string, or `${student_id}|${subject}|written`/`|oral` when split
  const [dirty,   setDirty]   = useState(false);
  const [msg,     setMsg]     = useState('');
  const [busy,    setBusy]    = useState(false);
  const [classSubjects, setClassSubjects] = useState([]); // canonical subjects for this class, curated once by admin
  const [newSub,  setNewSub]  = useState({ subject: '', max_marks: 100, category: 'academic' });
  const [addingNewSubject, setAddingNewSubject] = useState(false); // true = typing a brand-new subject name
  const [card,    setCard]    = useState(null);      // { student, exams } for report card
  const [cardFormat, setCardFormat] = useState('standard'); // 'standard' | 'traditional' — picked when opening a report card
  const [sheet,   setSheet]   = useState(false);

  useEffect(() => {
    getClasses().then(cs => { setClasses(cs); if (cs.length) setClassId(String(cs[0].id)); })
      .catch(err => setMsg('Error: ' + (err.response?.data?.error || err.message)));
  }, []);

  useEffect(() => {
    if (!classId) return;
    setExamId(null); setGrid(null); setMarks({}); setDirty(false);
    getClassExams(classId).then(setExams).catch(() => setExams([]));
    getSubjects(classId).then(setClassSubjects).catch(() => setClassSubjects([]));
  }, [classId]);

  function confirmDiscard() {
    return !dirty || window.confirm('You have unsaved marks. Discard them?');
  }

  async function openExam(id) {
    if (!confirmDiscard()) return;
    setMsg(''); setExamId(id);
    try {
      const g = await getExamGrid(classId, id);
      setGrid(g);
      const m = {};
      for (const st of g.students) {
        for (const [sub, v] of Object.entries(st.marks)) m[`${st.student_id}|${sub}`] = String(v);
        for (const [sub, detail] of Object.entries(st.marksDetail || {})) {
          if (detail.written != null) m[`${st.student_id}|${sub}|written`] = String(detail.written);
          if (detail.oral != null) m[`${st.student_id}|${sub}|oral`] = String(detail.oral);
        }
      }
      setMarks(m); setDirty(false);
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  async function startNew(presetName, presetHasOral) {
    if (!confirmDiscard()) return;
    setMsg('');
    try {
      const roster = await getClassRoster(classId);
      setExamId('new');
      setGrid({
        exam: { name: presetName || '', exam_date: todayISO(), has_oral: !!presetHasOral },
        subjects: [],
        students: roster.students.map(s => ({ student_id: s.id, name: s.name, roll_number: s.roll_number, marks: {} })),
      });
      setMarks({}); setDirty(false);
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  // A quick-start pill opens the matching exam if it already exists (by the
  // same name-matching rule the RBSE report card uses), or starts a new one
  // pre-named and pre-set to Written+Oral entry — that's every RBSE slot.
  function openRbseSlot(slot) {
    const existing = exams.find(e => slot.test(e.name));
    if (existing) return openExam(existing.id);
    const preset = RBSE_QUICK_START.find(q => q.key === slot.key);
    startNew(preset?.name, true);
  }

  function addToGrid(subject) {
    if (!subject || grid.subjects.some(s => s.subject.toLowerCase() === subject.toLowerCase())) return;
    setGrid({ ...grid, subjects: [...grid.subjects, { subject, max_marks: Number(newSub.max_marks) || 100, oral_max: null }] });
    setNewSub({ ...newSub, subject: '' });
    setAddingNewSubject(false);
    setDirty(true);
  }

  // Picking an existing canonical subject just adds it to this exam's grid
  // (no network call). Typing a brand-new one also saves it to the class's
  // subject list (via /subjects) so it's offered — and enforced — for every
  // future exam on this class, and in the staff app's result entry.
  async function addSubject(e) {
    e.preventDefault();
    const subject = newSub.subject.trim();
    if (!subject) return;
    // A dropdown pick is already canonical — just add it to this exam. Typed
    // text (either because the class has no subjects yet, or "+ New
    // subject…" was chosen) needs saving to the class's subject list first.
    const pickedFromList = !addingNewSubject && classSubjects.length > 0;
    if (pickedFromList) return addToGrid(subject);
    try {
      const created = await createSubject({ name: subject, class_id: Number(classId), category: newSub.category });
      setClassSubjects(cs => [...cs, created].sort((a, b) => a.name.localeCompare(b.name)));
      addToGrid(created.name);
    } catch (err) {
      if (err.response?.status === 409) {
        // Someone else already added this name — pick it up and use it.
        const fresh = await getSubjects(classId);
        setClassSubjects(fresh);
        const match = fresh.find(s => s.name.toLowerCase() === subject.toLowerCase());
        if (match) addToGrid(match.name);
      } else {
        setMsg('Error: ' + (err.response?.data?.error || err.message));
      }
    }
  }

  // Toggles an existing subject between academic/co-scholastic — used by the
  // Traditional report card's two-box layout; the Standard card ignores it.
  async function toggleCategory(subjectName) {
    const sub = classSubjects.find(s => s.name.toLowerCase() === subjectName.toLowerCase());
    if (!sub) return; // free-text subject, no canonical row to flag — Traditional card treats it as academic
    const next = sub.category === 'co_scholastic' ? 'academic' : 'co_scholastic';
    try {
      const updated = await patchSubject(sub.id, { category: next });
      setClassSubjects(cs => cs.map(s => s.id === sub.id ? updated : s));
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  function removeSubject(subject) {
    const hasMarks = Object.keys(marks).some(k => k.endsWith(`|${subject}`) && marks[k] !== '');
    if (hasMarks && !window.confirm(`Remove ${subject} from the grid? Marks already saved for it stay on record.`)) return;
    setGrid({ ...grid, subjects: grid.subjects.filter(s => s.subject !== subject) });
  }

  function setMax(subject, value) {
    setGrid({ ...grid, subjects: grid.subjects.map(s => s.subject === subject ? { ...s, max_marks: value } : s) });
    setDirty(true);
  }

  function setOralMax(subject, value) {
    setGrid({ ...grid, subjects: grid.subjects.map(s => s.subject === subject ? { ...s, oral_max: value } : s) });
    setDirty(true);
  }

  // Live totals + rank (rank by percentage, ties share a rank). When the
  // exam has Written+Oral split entry, a subject's obtained marks come from
  // summing its written+oral cells instead of a single plain cell.
  const rows = useMemo(() => {
    if (!grid) return [];
    const hasOral = !!grid.exam.has_oral;
    const out = grid.students.map(st => {
      let obtained = 0, max = 0, filled = 0;
      for (const sub of grid.subjects) {
        if (hasOral) {
          const wv = marks[`${st.student_id}|${sub.subject}|written`];
          const ov = marks[`${st.student_id}|${sub.subject}|oral`];
          const has = (wv !== undefined && wv !== '') || (ov !== undefined && ov !== '');
          if (has && !Number.isNaN(Number(wv || 0)) && !Number.isNaN(Number(ov || 0))) {
            obtained += Number(wv || 0) + Number(ov || 0); max += Number(sub.max_marks) || 0; filled += 1;
          }
          continue;
        }
        const v = marks[`${st.student_id}|${sub.subject}`];
        if (v !== undefined && v !== '' && !Number.isNaN(Number(v))) {
          obtained += Number(v); max += Number(sub.max_marks) || 0; filled += 1;
        }
      }
      return { ...st, obtained, max, filled, pct: filled ? pct(obtained, max) : null };
    });
    const ranked = out.filter(r => r.pct != null).map(r => r.pct).sort((a, b) => b - a);
    return out.map(r => ({ ...r, rank: r.pct == null ? null : ranked.indexOf(r.pct) + 1 }));
  }, [grid, marks]);

  async function save() {
    const name = grid.exam.name.trim();
    if (!name) return setMsg('Error: give the exam a name');
    if (!grid.subjects.length) return setMsg('Error: add at least one subject');
    const hasOral = !!grid.exam.has_oral;
    if (hasOral) {
      for (const sub of grid.subjects) {
        const oralMax = Number(sub.oral_max);
        if (!(oralMax >= 0) || oralMax >= Number(sub.max_marks)) {
          return setMsg(`Error: set an oral max less than ${sub.max_marks} for ${sub.subject}`);
        }
      }
    }
    const subjects = grid.subjects.map(s => ({ subject: s.subject, max_marks: Number(s.max_marks) }));
    if (subjects.some(s => !(s.max_marks > 0))) return setMsg('Error: every subject needs max marks above 0');

    // Collect every bad cell in one pass instead of stopping at the first —
    // with Written+Oral entry across a whole class grid, a stray out-of-range
    // value is rarely the only one, and fixing them one error-popup at a time
    // is tedious.
    const payload = [];
    const errors = [];
    for (const st of grid.students) {
      for (const sub of grid.subjects) {
        const max = Number(sub.max_marks);
        if (hasOral) {
          const oralMax = Number(sub.oral_max) || 0;
          const writtenMax = max - oralMax;
          const wv = marks[`${st.student_id}|${sub.subject}|written`];
          const ov = marks[`${st.student_id}|${sub.subject}|oral`];
          if ((wv === undefined || wv === '') && (ov === undefined || ov === '')) continue;
          const wn = wv === undefined || wv === '' ? 0 : Number(wv);
          const on = ov === undefined || ov === '' ? 0 : Number(ov);
          let bad = false;
          if (Number.isNaN(wn) || wn < 0 || wn > writtenMax) {
            errors.push(`${st.name}'s ${sub.subject} written mark must be between 0 and ${writtenMax}`);
            bad = true;
          }
          if (Number.isNaN(on) || on < 0 || on > oralMax) {
            errors.push(`${st.name}'s ${sub.subject} oral mark must be between 0 and ${oralMax}`);
            bad = true;
          }
          if (bad) continue;
          payload.push({ student_id: st.student_id, subject: sub.subject, component: 'written', marks_obtained: wn, max_marks: writtenMax });
          payload.push({ student_id: st.student_id, subject: sub.subject, component: 'oral', marks_obtained: on, max_marks: oralMax });
          continue;
        }
        const v = marks[`${st.student_id}|${sub.subject}`];
        if (v === undefined || v === '') continue;
        const n = Number(v);
        if (Number.isNaN(n) || n < 0 || n > max) {
          errors.push(`${st.name}'s ${sub.subject} mark must be between 0 and ${max}`);
          continue;
        }
        payload.push({ student_id: st.student_id, subject: sub.subject, marks_obtained: n });
      }
    }
    if (errors.length) {
      return setMsg(`Error: ${errors.length} mark${errors.length > 1 ? 's' : ''} out of range — ${errors.join('; ')}`);
    }

    setBusy(true); setMsg('');
    try {
      const r = await saveExamMarks(classId, { exam_name: name, exam_date: grid.exam.exam_date || null, subjects, marks: payload, has_oral: hasOral });
      setMsg(`Saved ${r.updated} marks ✅`);
      setDirty(false);
      const list = await getClassExams(classId);
      setExams(list);
      setExamId(r.exam_id);
    } catch (err) {
      setMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally { setBusy(false); }
  }

  async function removeExam() {
    if (!window.confirm(`Delete "${grid.exam.name}" and every mark entered for it? This can't be undone.`)) return;
    try {
      await deleteExam(examId);
      setExams(exams.filter(e => e.id !== examId));
      setExamId(null); setGrid(null); setMarks({}); setDirty(false);
      setMsg('Exam deleted');
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  async function openReportCard(studentId, format = 'standard') {
    try {
      const [profile, results] = await Promise.all([getStudentProfile(studentId), getStudentResults(studentId)]);
      if (!results.length) return setMsg('Error: no saved results for this student yet');
      setCardFormat(format);
      setCard({ student: profile.student, exams: results });
    } catch (err) { setMsg('Error: ' + (err.response?.data?.error || err.message)); }
  }

  const className = classes.find(c => String(c.id) === classId)?.name;

  return (
    <div>
      <h2 className="font-display font-extrabold text-xl text-ink mb-4">Exams &amp; Results</h2>

      <Card className="p-4 mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Class
          <select className={inputCls} value={classId} onChange={e => { if (confirmDiscard()) setClassId(e.target.value); }}>
            {!classes.length && <option value="">No classes yet</option>}
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <div className="flex flex-wrap gap-2 items-center">
          {exams.map(ex => (
            <button key={ex.id} onClick={() => openExam(ex.id)}
              className={`px-3 py-2 rounded-s text-[13px] font-bold border transition-colors
                ${examId === ex.id ? 'bg-transit text-white border-transit' : 'bg-paper-raised text-ink border-line hover:border-transit'}`}>
              {ex.name} <span className="font-semibold opacity-75">· {fmtDate(ex.exam_date)}</span>
            </button>
          ))}
          <Button variant={examId === 'new' ? 'primary' : 'secondary'} onClick={() => startNew()} disabled={!classId} className="flex items-center gap-1.5">
            <Plus size={14} /> New exam
          </Button>
        </div>
      </Card>

      {classId && (
        <Card className="p-3 mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold text-ink-soft">RBSE report card exams:</span>
          {REPORT_SLOTS.map(slot => {
            const existing = exams.find(e => slot.test(e.name));
            const isOpen = existing ? examId === existing.id : false;
            return (
              <button key={slot.key} onClick={() => openRbseSlot(slot)}
                title={existing ? `Open "${existing.name}"` : `Start a new exam named "${RBSE_QUICK_START.find(q => q.key === slot.key).name}" (the name the RBSE report card looks for)`}
                className={`px-2.5 py-1.5 rounded-s text-[12px] font-bold border transition-colors
                  ${isOpen ? 'bg-transit text-white border-transit' : existing ? 'bg-success-tint text-success border-success' : 'bg-paper-raised text-ink-soft border-line hover:border-transit'}`}>
                {existing ? '✓ ' : '+ '}{slot.label}
              </button>
            );
          })}
        </Card>
      )}

      {msg && (
        <p className={`text-sm font-bold px-3 py-2 rounded-s w-fit mb-3 ${msg.startsWith('Error') ? 'bg-critical-tint text-critical' : 'bg-success-tint text-success'}`}>{msg}</p>
      )}

      {!grid && classId && (
        <p className="text-sm text-ink-soft">
          {exams.length ? 'Pick an exam above to see or edit its marks, or start a new one.' : `No exams for class ${className || ''} yet — start one with New exam.`}
        </p>
      )}

      {grid && (
        <Card>
          <div className="p-4 border-b border-line-soft flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Exam name
              <input className={inputCls} placeholder="e.g. Half Yearly" value={grid.exam.name} disabled={examId !== 'new'}
                onChange={e => { setGrid({ ...grid, exam: { ...grid.exam, name: e.target.value } }); setDirty(true); }} />
            </label>
            <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Date
              <input type="date" className={inputCls} value={grid.exam.exam_date || ''}
                onChange={e => { setGrid({ ...grid, exam: { ...grid.exam, exam_date: e.target.value } }); setDirty(true); }} />
            </label>
            <label className="flex items-center gap-1.5 text-xs font-bold text-ink-soft mb-2.5" title="Enter Written and Oral marks separately for every subject in this exam — needed for the Traditional report card format">
              <input type="checkbox" checked={!!grid.exam.has_oral}
                onChange={e => { setGrid({ ...grid, exam: { ...grid.exam, has_oral: e.target.checked } }); setDirty(true); }} />
              Written + Oral marks
            </label>
            <form onSubmit={addSubject} className="flex items-end gap-2">
              <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Add subject
                {addingNewSubject || !classSubjects.length ? (
                  <input className={`${inputCls} w-36`} placeholder="e.g. Maths" value={newSub.subject} autoFocus={addingNewSubject}
                    onChange={e => setNewSub({ ...newSub, subject: e.target.value })} />
                ) : (
                  <select className={`${inputCls} w-36`} value={newSub.subject}
                    onChange={e => {
                      if (e.target.value === '__new__') { setAddingNewSubject(true); setNewSub({ ...newSub, subject: '' }); }
                      else setNewSub({ ...newSub, subject: e.target.value });
                    }}>
                    <option value="">Choose…</option>
                    {classSubjects
                      .filter(s => !grid.subjects.some(g => g.subject.toLowerCase() === s.name.toLowerCase()))
                      .map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                    <option value="__new__">+ New subject…</option>
                  </select>
                )}
              </label>
              {addingNewSubject && classSubjects.length > 0 && (
                <button type="button" onClick={() => { setAddingNewSubject(false); setNewSub({ ...newSub, subject: '' }); }}
                  className="text-xs text-ink-soft hover:underline mb-2.5">Pick from list</button>
              )}
              <label className="flex flex-col gap-1 text-xs font-bold text-ink-soft">Max
                <input type="number" min="1" className={`${inputCls} w-20`} value={newSub.max_marks}
                  onChange={e => setNewSub({ ...newSub, max_marks: e.target.value })} />
              </label>
              <label className="flex items-center gap-1.5 text-xs font-bold text-ink-soft mb-2.5" title="Shown in a separate box on the Traditional report card format">
                <input type="checkbox" checked={newSub.category === 'co_scholastic'}
                  onChange={e => setNewSub({ ...newSub, category: e.target.checked ? 'co_scholastic' : 'academic' })} />
                Co-scholastic
              </label>
              <Button type="submit" variant="secondary" className="!px-3"><Plus size={15} /></Button>
            </form>
            <div className="flex gap-2 ml-auto">
              {examId !== 'new' && (
                <>
                  <Button variant="secondary" onClick={() => setSheet(true)} className="flex items-center gap-1.5"><Printer size={14} /> Result sheet</Button>
                  <Button variant="danger" onClick={removeExam} className="flex items-center gap-1.5"><Trash2 size={14} /> Delete</Button>
                </>
              )}
              <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save marks'}</Button>
            </div>
          </div>

          {!grid.students.length && <p className="px-5 py-6 text-sm text-ink-soft">No students in this class yet.</p>}
          {grid.students.length > 0 && !grid.subjects.length && (
            <p className="px-5 py-6 text-sm text-ink-soft">Add the subjects for this exam above, then fill in marks.</p>
          )}

          {grid.students.length > 0 && grid.subjects.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] font-bold uppercase tracking-wider text-ink-soft bg-paper">
                    <th className="px-3 py-2.5 sticky left-0 bg-paper">Student</th>
                    {grid.subjects.map(sub => {
                      const cat = classSubjects.find(s => s.name.toLowerCase() === sub.subject.toLowerCase())?.category;
                      return (
                        <th key={sub.subject} className="px-2 py-2 text-center min-w-[96px]">
                          <div className="flex items-center justify-center gap-1">
                            {sub.subject}
                            <button onClick={() => removeSubject(sub.subject)} aria-label={`Remove ${sub.subject}`} className="hover:text-critical"><X size={12} /></button>
                          </div>
                          {cat && (
                            <button onClick={() => toggleCategory(sub.subject)} title="Click to toggle for the Traditional report card"
                              className="block mx-auto mt-0.5 text-[9px] font-bold normal-case tracking-normal text-ink-soft hover:text-transit-deep underline">
                              {cat === 'co_scholastic' ? 'Co-scholastic' : 'Academic'}
                            </button>
                          )}
                          <input type="number" min="1" value={sub.max_marks} onChange={e => setMax(sub.subject, e.target.value)}
                            aria-label={`${sub.subject} max marks`} title="Max marks"
                            className="mt-1 w-16 text-center border border-line rounded-s px-1 py-0.5 text-xs bg-paper-raised normal-case" />
                          {grid.exam.has_oral && (
                            <>
                              <input type="number" min="0" value={sub.oral_max ?? ''} onChange={e => setOralMax(sub.subject, e.target.value)}
                                aria-label={`${sub.subject} oral max`} title="Oral max (out of the Max above)" placeholder="Oral max"
                                className="mt-1 w-16 text-center border border-line rounded-s px-1 py-0.5 text-xs bg-paper-raised normal-case" />
                              <div className="mt-0.5 text-[9px] font-bold normal-case tracking-normal text-ink-soft">
                                Written ≤ {Number(sub.max_marks) - (Number(sub.oral_max) || 0)}
                              </div>
                            </>
                          )}
                        </th>
                      );
                    })}
                    <th className="px-3 py-2.5 text-right">Total</th>
                    <th className="px-3 py-2.5 text-right">%</th>
                    <th className="px-3 py-2.5 text-center">{!board || board === 'CBSE' ? 'Grade' : 'Result'}</th>
                    <th className="px-3 py-2.5 text-center">Rank</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(st => (
                    <tr key={st.student_id} className="border-t border-line-soft">
                      <td className="px-3 py-2 sticky left-0 bg-paper-raised">
                        <button onClick={() => onOpenStudent(st.student_id, 'results')} className="font-semibold text-transit-deep hover:underline text-left">{st.name}</button>
                        {st.roll_number && <span className="block text-xs text-ink-soft">Roll {st.roll_number}</span>}
                      </td>
                      {grid.subjects.map(sub => {
                        if (grid.exam.has_oral) {
                          const oralMax = Number(sub.oral_max) || 0;
                          const writtenMax = Number(sub.max_marks) - oralMax;
                          const wKey = `${st.student_id}|${sub.subject}|written`;
                          const oKey = `${st.student_id}|${sub.subject}|oral`;
                          const wv = marks[wKey] ?? '';
                          const ov = marks[oKey] ?? '';
                          const wBad = wv !== '' && (Number.isNaN(Number(wv)) || Number(wv) < 0 || Number(wv) > writtenMax);
                          const oBad = ov !== '' && (Number.isNaN(Number(ov)) || Number(ov) < 0 || Number(ov) > oralMax);
                          return (
                            <td key={sub.subject} className="px-2 py-1.5 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <input type="number" min="0" max={writtenMax} step="any" value={wv} title="Written"
                                  aria-label={`${st.name} ${sub.subject} written`}
                                  onChange={e => { setMarks({ ...marks, [wKey]: e.target.value }); setDirty(true); }}
                                  className={`w-11 text-center border rounded-s px-1 py-1.5 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit
                                    ${wBad ? 'border-critical text-critical' : 'border-line'}`} />
                                <input type="number" min="0" max={oralMax} step="any" value={ov} title="Oral"
                                  aria-label={`${st.name} ${sub.subject} oral`}
                                  onChange={e => { setMarks({ ...marks, [oKey]: e.target.value }); setDirty(true); }}
                                  className={`w-11 text-center border rounded-s px-1 py-1.5 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit
                                    ${oBad ? 'border-critical text-critical' : 'border-line'}`} />
                              </div>
                            </td>
                          );
                        }
                        const key = `${st.student_id}|${sub.subject}`;
                        const v = marks[key] ?? '';
                        const bad = v !== '' && (Number.isNaN(Number(v)) || Number(v) < 0 || Number(v) > Number(sub.max_marks));
                        return (
                          <td key={sub.subject} className="px-2 py-1.5 text-center">
                            <input type="number" min="0" max={sub.max_marks} step="any" value={v}
                              aria-label={`${st.name} ${sub.subject}`}
                              onChange={e => { setMarks({ ...marks, [key]: e.target.value }); setDirty(true); }}
                              className={`w-16 text-center border rounded-s px-1 py-1.5 text-sm bg-paper-raised text-ink focus:outline-none focus:ring-2 focus:ring-transit
                                ${bad ? 'border-critical text-critical' : 'border-line'}`} />
                          </td>
                        );
                      })}
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{st.filled ? `${st.obtained}/${st.max}` : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{st.pct ?? '—'}</td>
                      <td className="px-3 py-2 text-center font-bold text-transit-deep">{resultFor(st.pct, board)}</td>
                      <td className="px-3 py-2 text-center tabular-nums">{st.rank ?? '—'}</td>
                      <td className="px-3 py-2">
                        {examId !== 'new' && (
                          <div className="flex items-center gap-1">
                            <Button variant="secondary" className="!px-2.5 !py-1 !text-xs flex items-center gap-1" onClick={() => openReportCard(st.student_id, 'standard')}>
                              <FileText size={12} /> Report card
                            </Button>
                            <button onClick={() => openReportCard(st.student_id, 'traditional')} title="RBSE-style periodic report card (First/Second/Third/Half Yearly/Annual, Written-Oral split, co-scholastic grades)"
                              className="text-[11px] font-bold text-ink-soft hover:text-transit-deep underline">RBSE format</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {dirty && <p className="px-5 py-3 text-xs font-bold text-caution border-t border-line-soft">Unsaved changes — press Save marks.</p>}
        </Card>
      )}

      {card && cardFormat === 'traditional' && <TraditionalReportCardSheet student={card.student} exams={card.exams} schoolName={schoolName} board={board} onClose={() => setCard(null)} />}
      {card && cardFormat !== 'traditional' && <ReportCardSheet student={card.student} exams={card.exams} schoolName={schoolName} board={board} onClose={() => setCard(null)} />}

      {sheet && grid && (
        <PrintSheet title="Class result sheet" onClose={() => setSheet(false)}>
          <PrintHeader schoolName={schoolName} docTitle={`Result Sheet — ${grid.exam.name}`}
            right={<>Class <b>{className}</b><br />{fmtDate(grid.exam.exam_date)}</>} />
          <table className="w-full text-[12.5px] border-collapse">
            <thead>
              <tr>
                <th className="text-left">Roll</th><th className="text-left">Student</th>
                {grid.subjects.map(s => <th key={s.subject} className="text-center">{s.subject}<br /><span className="font-normal">/{s.max_marks}</span></th>)}
                <th className="text-center">Total</th><th className="text-center">%</th><th className="text-center">{!board || board === 'CBSE' ? 'Grade' : 'Result'}</th><th className="text-center">Rank</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(st => (
                <tr key={st.student_id}>
                  <td>{st.roll_number || ''}</td><td>{st.name}</td>
                  {grid.subjects.map(s => <td key={s.subject} className="text-center">{marks[`${st.student_id}|${s.subject}`] || '—'}</td>)}
                  <td className="text-center">{st.filled ? `${st.obtained}/${st.max}` : '—'}</td>
                  <td className="text-center">{st.pct ?? '—'}</td>
                  <td className="text-center">{resultFor(st.pct, board)}</td>
                  <td className="text-center">{st.rank ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrintSheet>
      )}
    </div>
  );
}
